import React, { Suspense, useMemo, useState } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import {
  FiInfo,
  FiLogOut,
  FiMenu,
  FiMoon,
  FiRefreshCw,
  FiSearch,
  FiServer,
  FiSettings,
  FiSun,
  FiUser,
  FiX
} from 'react-icons/fi'
import { useAuthStore, useAppStore } from '../store'
import clsx from 'clsx'
import GoalsAlertMonitor from './GoalsAlertMonitor'
import { authService } from '../services/api'
import toast from 'react-hot-toast'
import AlertBell from './AlertBell'
import SofiaChatWidget from './SofiaChatWidget'
import { useVersion } from '../hooks/useVersion'
import useRealtimeConnection from '../hooks/useRealtimeConnection'
import { getNavGroups } from '../routes/registry'

const Layout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, switchTenant } = useAuthStore()
  const { sidebarOpen, toggleSidebar, darkMode, toggleDarkMode } = useAppStore()
  const sofiaWidgetEnabled = import.meta.env.VITE_ENABLE_SOFIA_WIDGET !== 'false'
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [submenuOpen, setSubmenuOpen] = useState({})
  const [switchingTenant, setSwitchingTenant] = useState(false)
  const [alertsCount, setAlertsCount] = useState(0)
  const version = useVersion()

  useRealtimeConnection({ enabled: user?.tenant?.planType >= 2 })

  const navGroups = useMemo(() => getNavGroups(user), [user])
  const outletKey = user?.tenant?.id || 'no-tenant'

  const handleSwitchTenant = async (tenantId) => {
    try {
      setSwitchingTenant(true)
      const response = await authService.switchTenant(tenantId)
      switchTenant(response.data)
      toast.success(`Conectado à empresa: ${response.data.tenant.nome}`)
      setUserMenuOpen(false)
      navigate('/')
    } catch (error) {
      console.error('Erro ao trocar tenant:', error)
      toast.error(error.response?.data?.error || 'Erro ao trocar de empresa')
    } finally {
      setSwitchingTenant(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path
    return location.pathname.startsWith(path)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Goals Alert Monitor */}
      <GoalsAlertMonitor />

      {/* SofIA Chat Widget (Plano Pro+) */}
      {sofiaWidgetEnabled && user?.tenant?.planType >= 3 && <SofiaChatWidget />}

      {/* Sidebar Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        'sidebar',
        !sidebarOpen && 'collapsed'
      )}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
            <Link to="/" className="flex items-center gap-3">
              {!sidebarOpen ? (
                <>
                  <img
                    src="/logo.png"
                    alt="AlBaaOne Monitor"
                    className="h-12 w-auto dark:hidden"
                  />
                  <img
                    src="/logo-dark.png"
                    alt="AlBaaOne Monitor"
                    className="h-12 w-auto hidden dark:block"
                  />
                </>
              ) : (
                <img
                  src="/logo-icon.png"
                  alt="AlBaaOne"
                  className="h-10 w-10"
                />
              )}
            </Link>
            <button
              onClick={toggleSidebar}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Menu */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <ul className="space-y-2">
              {navGroups.map((group, groupIdx) => (
                <React.Fragment key={group.id}>
                  {sidebarOpen && (
                    <li className={clsx(groupIdx > 0 && 'pt-4 mt-4 border-t dark:border-gray-700')}>
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase px-3 mb-2">
                        {group.label}
                      </p>
                    </li>
                  )}

                  {group.items.map((item) => {
                    const hasChildren = Array.isArray(item.children) && item.children.length > 0
                    const submenuKey = item.path
                    const parentActive = hasChildren
                      ? item.children.some((c) => isActive(c.path, c.exact)) || isActive(item.path, item.exact)
                      : isActive(item.path, item.exact)
                    const badge = item.id === 'alerts' && alertsCount > 0 ? alertsCount : null

                    return (
                      <li key={item.id}>
                        {hasChildren ? (
                          <>
                            <button
                              onClick={() => setSubmenuOpen({ ...submenuOpen, [submenuKey]: !submenuOpen[submenuKey] })}
                              className={clsx(
                                'sidebar-item rounded-lg w-full',
                                parentActive && 'active',
                                !sidebarOpen && 'justify-center'
                              )}
                              title={!sidebarOpen ? item.label : ''}
                              type="button"
                            >
                              {item.icon && <item.icon className="w-5 h-5 flex-shrink-0" />}
                              {sidebarOpen && (
                                <>
                                  <span className="ml-3 flex-1">{item.label}</span>
                                  <span className={clsx('transform transition-transform', submenuOpen[submenuKey] && 'rotate-90')}>▶</span>
                                </>
                              )}
                            </button>
                            {sidebarOpen && submenuOpen[submenuKey] && (
                              <ul className="ml-8 mt-2 space-y-1">
                                {item.children.map((subitem) => (
                                  <li key={subitem.id || subitem.path}>
                                    <Link
                                      to={subitem.path}
                                      className={clsx(
                                        'sidebar-item rounded-lg text-sm',
                                        isActive(subitem.path, subitem.exact) && 'active'
                                      )}
                                    >
                                      {subitem.icon && <subitem.icon className="w-4 h-4 mr-2 inline" />}
                                      {subitem.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </>
                        ) : (
                          <Link
                            to={item.path}
                            className={clsx(
                              'sidebar-item rounded-lg',
                              isActive(item.path, item.exact) && 'active',
                              !sidebarOpen && 'justify-center'
                            )}
                            title={!sidebarOpen ? item.label : ''}
                          >
                            {item.icon && <item.icon className="w-5 h-5 flex-shrink-0" />}
                            {sidebarOpen && (
                              <>
                                <span className="ml-3 flex-1">{item.label}</span>
                                {badge && (
                                  <span className="badge badge-primary">{badge > 99 ? '99+' : badge}</span>
                                )}
                              </>
                            )}
                          </Link>
                        )}
                      </li>
                    )
                  })}
                </React.Fragment>
              ))}
            </ul>
          </nav>

          {/* User Info */}
          {sidebarOpen && user && (
            <div className="p-4 border-t dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                  <FiUser className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {user.nome || user.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                    {user.role}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className={clsx(
        'transition-all duration-300',
        sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
      )}>
        {/* Top Bar */}
        <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleSidebar}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FiMenu className="w-6 h-6" />
              </button>

              {/* Search */}
              <div className="hidden md:block">
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    className="pl-10 pr-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Tenant Name */}
              {user?.tenant && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                  <FiServer className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    {user.tenant.nome}
                  </span>
                </div>
              )}

              {version?.label && (
                <div className="hidden md:flex items-center gap-2 px-3 py-1 text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <FiInfo className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  <span className="truncate">Versão {version.label}</span>
                </div>
              )}

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleDarkMode}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {darkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
              </button>

              {/* Notifications */}
              {user?.tenant?.planType >= 2 && (
                <AlertBell onCountChange={setAlertsCount} />
              )}

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
                >
                  <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <FiUser className="w-4 h-4" />
                  </div>
                  <span className="hidden md:block text-sm font-medium">{user?.nome || user?.email}</span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 z-50">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {user?.nome || user?.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {user?.email}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 capitalize mt-1">
                        {user?.role}
                      </p>
                    </div>

                    {/* Tenant Switcher */}
                    {user?.tenants && user.tenants.length > 1 && (
                      <>
                        <div className="px-4 py-2">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">
                            Mudar Empresa
                          </p>
                          {user.tenants.map((tenant) => (
                            <button
                              key={tenant.id}
                              onClick={() => handleSwitchTenant(tenant.id)}
                              disabled={switchingTenant || tenant.id === user.tenant?.id}
                              className={clsx(
                                'flex items-center gap-2 w-full px-3 py-2 text-sm rounded-lg mb-1',
                                tenant.id === user.tenant?.id
                                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-medium'
                                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700',
                                switchingTenant && 'opacity-50 cursor-not-allowed'
                              )}
                            >
                              {switchingTenant && tenant.id !== user.tenant?.id ? (
                                <FiRefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <FiServer className="w-4 h-4" />
                              )}
                              <span className="truncate">{tenant.nome}</span>
                              {tenant.id === user.tenant?.id && (
                                <span className="ml-auto text-xs">✓</span>
                              )}
                            </button>
                          ))}
                        </div>
                        <div className="border-t dark:border-gray-700 my-1"></div>
                      </>
                    )}

                    {/* Menu Items */}
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <FiSettings className="w-4 h-4" />
                      Configurações
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <FiLogOut className="w-4 h-4" />
                      Sair
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Suspense
            fallback={(
              <div className="max-w-7xl mx-auto">
                <div className="card">
                  <div className="text-sm text-gray-600 dark:text-gray-300">Carregando...</div>
                </div>
              </div>
            )}
          >
            <Outlet key={outletKey} />
          </Suspense>
        </main>
      </div>
    </div>
  )
}

export default Layout
